const STORAGE_PREFIX = "ConfigurableDataTableState";
const STORAGE_VERSION = 1;
export const MIN_CONFIGURABLE_COLUMN_WIDTH = 5;

const normalizeWidths = (columnKeys, widths, minimumWidths = {}) => {
  const availableKeys = new Set(columnKeys);

  return Object.entries(widths ?? {}).reduce((normalized, [key, width]) => {
    const numericWidth = Number(width);
    if (availableKeys.has(key) && Number.isFinite(numericWidth)) {
      const configuredMinimum = Number(minimumWidths[key]);
      const minimum = Number.isFinite(configuredMinimum)
        ? configuredMinimum
        : MIN_CONFIGURABLE_COLUMN_WIDTH;
      normalized[key] = Math.max(
        minimum,
        numericWidth,
      );
    }
    return normalized;
  }, {});
};

export const getConfigurableDataTableStorageKey = (id) =>
  id ? `${STORAGE_PREFIX}:${encodeURIComponent(id)}` : null;

export const createDefaultColumnSettings = (columnKeys) => ({
  order: [...columnKeys],
  visibleKeys: [...columnKeys],
  widths: {},
});

export const reconcileColumnSettings = (
  columnKeys,
  settings,
  minimumWidths = {},
) => {
  const availableKeys = new Set(columnKeys);
  const persistedOrder = (settings?.order || []).filter((key) =>
    availableKeys.has(key),
  );
  const newKeys = columnKeys.filter((key) => !persistedOrder.includes(key));
  const order = [...persistedOrder, ...newKeys];
  const persistedVisible = new Set(
    (settings?.visibleKeys || []).filter((key) => availableKeys.has(key)),
  );

  newKeys.forEach((key) => persistedVisible.add(key));

  return {
    order,
    visibleKeys:
      settings && Array.isArray(settings.visibleKeys)
        ? order.filter((key) => persistedVisible.has(key))
        : [...order],
    widths: normalizeWidths(columnKeys, settings?.widths, minimumWidths),
  };
};

export const readColumnSettings = (
  storageKey,
  columnKeys,
  minimumWidths = {},
) => {
  if (!storageKey || typeof window === "undefined") {
    return createDefaultColumnSettings(columnKeys);
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey));
    if (
      parsed?.version !== STORAGE_VERSION ||
      !Array.isArray(parsed.order) ||
      !Array.isArray(parsed.visibleKeys)
    ) {
      return createDefaultColumnSettings(columnKeys);
    }
    return reconcileColumnSettings(columnKeys, parsed, minimumWidths);
  } catch {
    return createDefaultColumnSettings(columnKeys);
  }
};

export const writeColumnSettings = (storageKey, settings) => {
  if (!storageKey || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ version: STORAGE_VERSION, ...settings }),
    );
  } catch {
    // Storage may be unavailable in private mode or when quota is exhausted.
  }
};
